'use client';
import { useState, useEffect } from 'react';
import { PropertyFilter } from '@src/v3/interfaces/utils/http';
import { parseURLToPropertyFilter } from '@src/v3/utils/helper';
import { useSearchParams } from 'next/navigation';
import { DEFAULT_FILTERS } from '@src/v3/constants/listingsFilter';
import {
  FilterTypeEnum,
  ListingsFilterProps,
} from '@src/v3/interfaces/components/listingsFilter';
import FilterDropdown from '@src/v3/components/coreUI/filterDropdown';
import RangeFilterDropdown from './rangeFilterDropdown';
import RoomsDropdown from './roomsDropdown';
import AmenitiesDropdown from './amenitiesDropdown';
import SortDropdown from './sortDropdown';
import { SORT_MAP } from '@src/v3/interfaces/app/propertyLists';
import {
  hasPriceFilterChanged,
  hasRoomsFilterChanged,
  hasAmenitiesFilterChanged,
  hasSizeFilterChanged,
  hasSortFilterChanged,
} from '@src/v3/utils/filterHelpers';

type FilterType = FilterTypeEnum | null;

const ListingsFilter = (props: ListingsFilterProps) => {
  const { applyFilters } = props;
  const searchParams = useSearchParams();
  const [filters, setFilters] = useState<PropertyFilter>(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] =
    useState<PropertyFilter>(DEFAULT_FILTERS);
  const [openFilter, setOpenFilter] = useState<FilterType>(null);

  // Handler to manage which filter is open (only one at a time)
  // When closing without applying, reset filters to previously applied values
  const handleFilterOpenChange = (filter: FilterType) => (isOpen: boolean) => {
    if (!isOpen) {
      // Dropdown is closing - reset filters to applied values
      setFilters(appliedFilters);
    }
    setOpenFilter(isOpen ? filter : null);
  };

  /**
   * Lifecycle hook that initializes filters from URL parameters when the component mounts.
   */
  useEffect(() => {
    // Parse URL parameters and set both filters and applied filters state
    if (searchParams) {
      const urlFilters = parseURLToPropertyFilter(searchParams);
      setFilters(urlFilters);
      setAppliedFilters(urlFilters); // URL params represent applied filters
    }
  }, [searchParams]);

  // Helper functions to check if filters are active (based on applied filters)
  const isPriceFilterActive = () => {
    return (
      appliedFilters.costMin !== DEFAULT_FILTERS.costMin ||
      appliedFilters.costMax !== DEFAULT_FILTERS.costMax
    );
  };

  const isRoomsFilterActive = () => {
    return (
      (appliedFilters.bedrooms?.length || 0) > 0 ||
      (appliedFilters.bathrooms?.length || 0) > 0
    );
  };

  const isAmenitiesFilterActive = () => {
    return appliedFilters.amenity && appliedFilters.amenity.length > 0;
  };

  const isSizeFilterActive = () => {
    return (
      appliedFilters.squareFootageMin !== DEFAULT_FILTERS.squareFootageMin ||
      appliedFilters.squareFootageMax !== DEFAULT_FILTERS.squareFootageMax
    );
  };

  const isSortFilterActive = () => {
    return appliedFilters.sortBy !== DEFAULT_FILTERS.sortBy;
  };

  return (
    <div className="flex items-center gap-3 flex-shrink-0">
      {/* Price Filter */}
      <FilterDropdown
        label="Price"
        iconName="price"
        dropdownWidth="w-[440px]"
        isActive={isPriceFilterActive()}
        footerBtnDisabled={!hasPriceFilterChanged(filters)}
        open={openFilter === FilterTypeEnum.PRICE}
        onOpenChange={handleFilterOpenChange(FilterTypeEnum.PRICE)}
        onApplyFilter={() => {
          setAppliedFilters(filters); // Mark these filters as applied
          applyFilters(filters);
        }}
        onResetFilter={() => {
          const resetFilters = {
            ...filters,
            costMin: DEFAULT_FILTERS.costMin,
            costMax: DEFAULT_FILTERS.costMax,
          };
          setFilters(resetFilters);
          setAppliedFilters(resetFilters);
          applyFilters(resetFilters);
        }}
        content={
          <RangeFilterDropdown
            initialRange={{
              min: filters.costMin ?? 0,
              max: filters.costMax || 50000,
            }}
            title="Price"
            subTitle="Rental Prices, includes all fees and concessions"
            minRange={0}
            maxRange={50000}
            isCurrency
            onRangeChange={(value) => {
              setFilters({
                ...filters,
                costMin: value?.min ?? 0,
                costMax: value?.max || 50000,
              });
            }}
          />
        }
      />

      {/* Rooms Filter */}
      <FilterDropdown
        label="Rooms"
        iconName="room"
        dropdownWidth="w-[440px]"
        isActive={isRoomsFilterActive()}
        footerBtnDisabled={!hasRoomsFilterChanged(filters)}
        open={openFilter === FilterTypeEnum.ROOMS}
        onOpenChange={handleFilterOpenChange(FilterTypeEnum.ROOMS)}
        onApplyFilter={() => {
          setAppliedFilters(filters); // Mark these filters as applied
          applyFilters(filters);
        }}
        onResetFilter={() => {
          const resetFilters = {
            ...filters,
            bedrooms: DEFAULT_FILTERS.bedrooms,
            bathrooms: DEFAULT_FILTERS.bathrooms,
          };
          setFilters(resetFilters);
          setAppliedFilters(resetFilters);
          applyFilters(resetFilters);
        }}
        content={
          <RoomsDropdown
            selectedBeds={filters.bedrooms || []}
            selectedBathrooms={filters.bathrooms || []}
            onBedsChange={(beds) => {
              setFilters({
                ...filters,
                bedrooms: beds,
              });
            }}
            onBathroomsChange={(bathrooms) => {
              setFilters({
                ...filters,
                bathrooms: bathrooms,
              });
            }}
          />
        }
      />

      {/* Amenities Filter */}
      <FilterDropdown
        label="Amenities"
        iconName="amenities2"
        dropdownWidth="w-[480px]"
        isActive={isAmenitiesFilterActive()}
        footerBtnDisabled={!hasAmenitiesFilterChanged(filters)}
        open={openFilter === FilterTypeEnum.AMENITIES}
        onOpenChange={handleFilterOpenChange(FilterTypeEnum.AMENITIES)}
        onApplyFilter={() => {
          setAppliedFilters(filters); // Mark these filters as applied
          applyFilters(filters);
        }}
        onResetFilter={() => {
          const resetFilters = {
            ...filters,
            amenity: DEFAULT_FILTERS.amenity,
          };
          setFilters(resetFilters);
          setAppliedFilters(resetFilters);
          applyFilters(resetFilters);
        }}
        content={
          <AmenitiesDropdown
            selectedAmenities={filters.amenity || []}
            onAmenitiesChange={(amenities) => {
              setFilters({
                ...filters,
                amenity: amenities,
              });
            }}
          />
        }
      />

      {/* Size Filter */}
      <FilterDropdown
        label="Size"
        iconName="size"
        dropdownWidth="w-[442px]"
        isActive={isSizeFilterActive()}
        footerBtnDisabled={!hasSizeFilterChanged(filters)}
        open={openFilter === FilterTypeEnum.SIZE}
        onOpenChange={handleFilterOpenChange(FilterTypeEnum.SIZE)}
        onApplyFilter={() => {
          setAppliedFilters(filters); // Mark these filters as applied
          applyFilters(filters);
        }}
        onResetFilter={() => {
          const resetFilters = {
            ...filters,
            squareFootageMin: DEFAULT_FILTERS.squareFootageMin,
            squareFootageMax: DEFAULT_FILTERS.squareFootageMax,
          };
          setFilters(resetFilters);
          setAppliedFilters(resetFilters);
          applyFilters(resetFilters);
        }}
        content={
          <RangeFilterDropdown
            initialRange={{
              min: filters.squareFootageMin ?? 0,
              max: filters.squareFootageMax || 5000,
            }}
            title="Size"
            minRange={0}
            maxRange={5000}
            isCurrency={false}
            unit="sqft"
            onRangeChange={(value) => {
              setFilters({
                ...filters,
                squareFootageMin: value?.min ?? 0,
                squareFootageMax: value?.max || 5000,
              });
            }}
          />
        }
      />

      {/* Sort Filter */}
      <FilterDropdown
        label="Sort"
        iconName=""
        dropdownWidth="w-[300px]"
        isActive={isSortFilterActive()}
        footerBtnDisabled={!hasSortFilterChanged(filters)}
        open={openFilter === FilterTypeEnum.SORT}
        onOpenChange={handleFilterOpenChange(FilterTypeEnum.SORT)}
        onApplyFilter={() => {
          setAppliedFilters(filters); // Mark these filters as applied
          applyFilters(filters);
        }}
        onResetFilter={() => {
          const resetFilters = {
            ...filters,
            sortBy: DEFAULT_FILTERS.sortBy,
            sortLabel: DEFAULT_FILTERS.sortLabel,
          };
          setFilters(resetFilters);
          setAppliedFilters(resetFilters);
          applyFilters(resetFilters);
        }}
        content={
          <SortDropdown
            selectedSort={filters.sortLabel}
            onSortChange={(sortLabel) => {
              const sortBy = SORT_MAP[sortLabel as keyof typeof SORT_MAP] || '';

              setFilters({
                ...filters,
                sortBy: sortBy,
                sortLabel: sortLabel,
              });
            }}
          />
        }
      />
    </div>
  );
};

export default ListingsFilter;
